import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions, TablePagination,
  InputAdornment, CircularProgress, List, ListItem, ListItemText, ListItemIcon, Checkbox
} from '@mui/material';
import { toast } from 'react-toastify';
import baseURL from '../../../user/config';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';


// Microsoft Fluent UI-inspired colors
const MS_COLORS = {
  primary: '#2D4A3D',
  text: '#323130',
  textSecondary: '#605D5C',
  background: '#FFFFFF',
  border: '#E1E1E1',
  white: '#FFFFFF',
};

// Send Email Component
const SendEmailComponent = ({ open, handleClose, recipients }) => {
  const [emailContent, setEmailContent] = useState({ subject: '', message: '', attachments: [] });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEmailContent(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setEmailContent(prev => ({ ...prev, attachments: Array.from(e.target.files) }));
  };

const handleSendEmail = async () => {
    if (!emailContent.subject || !emailContent.subject.trim() || !emailContent.message || !emailContent.message.trim()) {
      toast.error("Subject and message cannot be empty.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('subject', emailContent.subject);
      formData.append('message', emailContent.message);
      formData.append('recipients', JSON.stringify(recipients));
      
      emailContent.attachments.forEach((file) => {
        formData.append('attachments', file);
      });

      const res = await fetch(`${baseURL}/org/email/send`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to send email.");
      }

      toast.success("Email sent successfully!");
      
      // ✅ Reset the email content fields after successful send
      setEmailContent({
        subject: '',
        message: '',
        attachments: []
      });
      
      handleClose();

    } catch (err) {
      console.error("Send email error:", err);
      toast.error(err.message || "Failed to send email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 'bold' }}>Compose Email</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Recipients: {recipients.join(', ')}
        </Typography>
        <TextField
          margin="dense"
          name="subject"
          label="Subject"
          type="text"
          fullWidth
          variant="outlined"
          value={emailContent.subject}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="dense"
          name="message"
          label="Message"
          type="text"
          fullWidth
          multiline
          rows={10}
          variant="outlined"
          value={emailContent.message}
          onChange={handleInputChange}
        />
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <Button
            variant="contained"
            component="label"
            sx={{
              backgroundColor: MS_COLORS.primary,
              color: MS_COLORS.white,
              textTransform: 'none',
              fontSize: "0.85rem",
              py: 1,
              px: 2,
              borderRadius: 1,
              boxShadow: 0,
              '&:hover': { backgroundColor: MS_COLORS.primary, opacity: 0.9, boxShadow: 0 },
            }}
          >
            Attach Files
            <input type="file" hidden onChange={handleFileChange} multiple />
          </Button>
          <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
            {emailContent.attachments.length > 0
              ? `${emailContent.attachments.length} file(s) selected`
              : 'No files attached'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions> <Button
            onClick={handleClose}
            sx={{
              backgroundColor: '#fff',
              color: MS_COLORS.primary,
              textTransform: 'none',
              fontSize: "0.85rem",
              py: 1,
              px: 2,
              borderRadius: 1,
              border: '1px solid #e1e1e1',
              boxShadow: 0,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={loading} // disable when loading
            sx={{
              backgroundColor: MS_COLORS.primary,
              color: MS_COLORS.white,
              textTransform: 'none',
              fontSize: "0.85rem",
              py: 1,
              px: 2,
              borderRadius: 1,
              boxShadow: 0,
              '&:hover': { backgroundColor: MS_COLORS.primary, opacity: 0.9, boxShadow: 0 },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: MS_COLORS.white }} />
            ) : (
              "Send Email"
            )}
          </Button>

      </DialogActions>
    </Dialog>
  );
};

const DonorManagementPage = () => {
  const [donors, setDonors] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [newDonor, setNewDonor] = useState({ firstName: '', lastName: '', email: '' });
  const [loading, setLoading] = useState(false);

  // State for search and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // State for recipient selection and email component
  const [openRecipientsDialog, setOpenRecipientsDialog] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [openEmailComponent, setOpenEmailComponent] = useState(false);


  useEffect(() => {
    fetchDonors();
  }, []);

  const fetchDonors = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseURL}/org/all-donors`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch donors');
      setDonors(data.donors || []);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to load donors');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewDonor(prev => ({ ...prev, [name]: value }));
  };

  const handleAddDonor = async () => {
    if (!newDonor.firstName || !newDonor.email) {
      toast.error("Please fill in first name and email.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${baseURL}/org/add-donor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newDonor),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to add donor.");
      toast.success("Donor added successfully!");
      setNewDonor({ firstName: '', lastName: '', email: '' });
      setOpenForm(false);
      fetchDonors(); // Refresh the donor list
    } catch (err) {
      console.error("Add donor error:", err);
      toast.error(err.message || "Failed to add donor.");
    } finally {
      setLoading(false);
    }
  };

  // Logic for search, sort, and pagination
  const sortedAndFilteredDonors = useMemo(() => {
    let sortableItems = [...donors];

    // Filter logic
    if (searchTerm) {
      sortableItems = sortableItems.filter(donor =>
        donor.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        donor.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        donor.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort logic
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableItems;
  }, [donors, searchTerm, sortConfig]);

  const donorsToDisplay = sortedAndFilteredDonors.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Pagination change handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page when rows per page changes
  };

  // Sorting handler
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    if (sortConfig.direction === 'asc') {
      return <ArrowUpwardIcon sx={{ fontSize: '1rem', ml: 1 }} />;
    } else {
      return <ArrowDownwardIcon sx={{ fontSize: '1rem', ml: 1 }} />;
    }
  };

  // Recipient selection handlers
const handleToggle = (email) => () => {
  const currentIndex = selectedEmails.indexOf(email);
  const newSelectedEmails = [...selectedEmails];

  if (currentIndex === -1) {
    newSelectedEmails.push(email);
  } else {
    newSelectedEmails.splice(currentIndex, 1);
  }

  setSelectedEmails(newSelectedEmails);
};

const handleSelectAllClick = (event) => {
  if (event.target.checked) {
    const newSelecteds = donors.map((n) => n.email);
    setSelectedEmails(newSelecteds);
    return;
  }
  setSelectedEmails([]);
};

const numSelected = selectedEmails.length;
const rowCount = donors.length;

const handleOpenRecipientsDialog = () => {
  setSelectedEmails([]);
  setOpenRecipientsDialog(true);
};

const handleDoneSelectingRecipients = () => {
  if (selectedEmails.length > 0) {
    setOpenRecipientsDialog(false);
    setOpenEmailComponent(true);
  } else {
    toast.warn("Please select at least one recipient.");
  }
};
  return (
    <Box 
      sx={{ 
        p: 4, 
        backgroundColor: MS_COLORS.background, 
        borderRadius: 2, 
        boxShadow: 0, 
        mt: 3, 
        border: `1px solid ${MS_COLORS.border}`,
        width: '100%',
        maxWidth: '100%' // Ensure full-width behavior
      }}
    >
      <Typography variant="h6" sx={{ color: MS_COLORS.text, fontWeight: "bold", mb: 2 }}>
        Donor Database
      </Typography>
      <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mb: 3 }}>
        Manage all your donors in one place. Anyone who has supported any of your campaigns is automatically added here.
      </Typography>

      {/* Action and Search bar */}
<Box 
  sx={{ 
    display: 'flex', 
    flexDirection: { xs: 'column', sm: 'row' }, // Stack on extra small screens
    justifyContent: 'space-between', 
    alignItems: { xs: 'flex-start', sm: 'center' }, // Align to start on small screens
    mb: 3, 
    gap: { xs: 2, sm: 0 } // Add vertical gap on small screens
  }}
>
    <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
            variant="contained"
            onClick={() => setOpenForm(true)}
            sx={{
                backgroundColor: MS_COLORS.primary,
                color: MS_COLORS.white,
                textTransform: 'none',
                fontSize: "0.85rem",
                py: 0.5,
                px: 2,
                borderRadius: 1,
                boxShadow: 0,
                '&:hover': { backgroundColor: MS_COLORS.primary, opacity: 0.9, boxShadow: 0 },
            }}
            startIcon={<Box component="span" sx={{ fontSize: '1rem', position: 'relative', top: '-1px' }}>+</Box>}
        >
            Add New Donor
        </Button>
        {donors.length > 0 && (
            <Button
                variant="outlined"
                onClick={handleOpenRecipientsDialog}
                sx={{
                    color: MS_COLORS.primary,
                    borderColor: MS_COLORS.primary,
                    textTransform: 'none',
                    fontSize: "0.85rem",
                    py: 0.5,
                    px: 2,
                    borderRadius: 1,
                    boxShadow: 0,
                    '&:hover': { borderColor: MS_COLORS.primary, backgroundColor: 'rgba(0, 120, 212, 0.04)' },
                }}
            >
                Send Email
            </Button>
        )}
    </Box>
    <TextField
  variant="outlined"
  placeholder="Search donors..."
  size="small"
  value={searchTerm}
  onChange={(e) => {
    setSearchTerm(e.target.value);
    setPage(0); // Reset page on search
  }}
  sx={{
    width: { xs: "100%", sm: "400px" },
    mt: { xs: 2, sm: 0 },
    "& .MuiOutlinedInput-root": {
      py: 0.5, // vertical padding
      "& fieldset": {
        borderColor: MS_COLORS.primary, // outline color
      },
      "&:hover fieldset": {
        borderColor: MS_COLORS.primary,
      },
      "&.Mui-focused fieldset": {
        borderColor: MS_COLORS.primary,
      },
    },
  }}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon />
      </InputAdornment>
    ),
  }}
/>

</Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : donors.length > 0 ? (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${MS_COLORS.border}` }}>
            <Table aria-label="donors table">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f4f4f4' }}>
                  <TableCell 
                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => requestSort('firstName')}
                  >
                    Name {getSortIcon('firstName')}
                  </TableCell>
                  <TableCell 
                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => requestSort('email')}
                  >
                    Email {getSortIcon('email')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {donorsToDisplay.length > 0 ? (
                  donorsToDisplay.map((donor) => (
                    <TableRow key={donor.id}>
                      <TableCell>{donor.firstName} {donor.lastName}</TableCell>
                      <TableCell>{donor.email}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                      No results found for your search.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={sortedAndFilteredDonors.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      ) : (
        <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', mt: 4, fontStyle: 'italic' }}>
          No donors found. Add a donor or wait for your first donation.
        </Typography>
      )}

      {/* Add New Donor Dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Add New Donor</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Please fill in the donor details below. All fields are required.
          </Typography>
          <TextField
            margin="dense"
            name="firstName"
            label="First Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newDonor.firstName}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="lastName"
            label="Last Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newDonor.lastName}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newDonor.email}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenForm(false)}
            sx={{
              backgroundColor: '#fff',
              color: MS_COLORS.primary,
              textTransform: 'none',
              fontSize: "0.85rem",
              py: 1,
              px: 2,
              borderRadius: 1,
              border: '1px solid #e1e1e1',
              boxShadow: 0,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddDonor}
            variant="contained"
            disabled={loading} // disable when loading
            sx={{
              backgroundColor: MS_COLORS.primary,
              color: MS_COLORS.white,
              textTransform: 'none',
              fontSize: "0.85rem",
              py: 1,
              px: 2,
              borderRadius: 1,
              boxShadow: 0,
              '&:hover': { backgroundColor: MS_COLORS.primary, opacity: 0.9, boxShadow: 0 },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: MS_COLORS.white }} />
            ) : (
              "Add Donor"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Recipient Selection Dialog */}
      <Dialog open={openRecipientsDialog} onClose={() => setOpenRecipientsDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Select Recipients ({numSelected} selected)
        </DialogTitle>
        <DialogContent>
          <List dense>
            {/* Select All Checkbox */}
            <ListItem sx={{ backgroundColor: '#f9f9f9' }}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  onChange={handleSelectAllClick}
                  checked={numSelected === rowCount && rowCount > 0}
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                />
              </ListItemIcon>
              <ListItemText 
                primary="Select All" 
                secondary={`${rowCount} donors in total`}
                sx={{ fontWeight: 'bold' }}
              />
            </ListItem>
            {/* List of Donors with Checkboxes */}
            {donors.map((donor) => (
              <ListItem
                key={donor.id}
                role="listitem"
                onClick={handleToggle(donor.email)}
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#fafafa' } }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedEmails.indexOf(donor.email) !== -1}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={`${donor.firstName} ${donor.lastName}`} secondary={donor.email} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          
          <Button
            onClick={() => setOpenRecipientsDialog(false)}
            sx={{
              backgroundColor: '#fff',
              color: MS_COLORS.primary,
              textTransform: 'none',
              fontSize: "0.85rem",
              py: 1,
              px: 2,
              borderRadius: 1,
              border: '1px solid #e1e1e1',
              boxShadow: 0,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDoneSelectingRecipients}
            variant="contained"
            disabled={loading} // disable when loading
            sx={{
              backgroundColor: MS_COLORS.primary,
              color: MS_COLORS.white,
              textTransform: 'none',
              fontSize: "0.85rem",
              py: 1,
              px: 2,
              borderRadius: 1,
              boxShadow: 0,
              '&:hover': { backgroundColor: MS_COLORS.primary, opacity: 0.9, boxShadow: 0 },
            }}
          >
              Send Email
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Email Composer Component */}
      <SendEmailComponent
        open={openEmailComponent}
        handleClose={() => setOpenEmailComponent(false)}
        recipients={selectedEmails}
      />
    </Box>
  );
};

export default DonorManagementPage;